import { useCallback, useContext, useMemo } from 'react';
import { Info } from 'react-feather';
import styled from '@emotion/styled';
import { Box } from '@mui/material';
import { ChatContextType, type ChatContextItem } from '@trinity/chat';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import {
  LinkContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { CustomPageContextContainer } from '@trinity/chat-components/components/CustomPageContext';
import { PageRenderer } from '@trinity/chat-components/components/PageDashboard/PageRenderer/index.js';
import AudioPlayerController from '@trinity/chat-components/components/TextToSpeechAudioPlayer/AudioPlayerController.js';
import { Constants } from '@trinity/chat-components/components/Theme';
import type { CodeWidgetHostActions } from '@trinity/chat-components/components/Widgets/CodeWidgetHostActionsContext';
import { CodeWidgetHostActionsContext } from '@trinity/chat-components/components/Widgets/CodeWidgetHostActionsContext';
import { RepDashboardProfileClickContext } from '@trinity/chat-components/components/Widgets/RepDashboardProfileClickContext';
import { RepDashboardSubmitQueryContext } from '@trinity/chat-components/components/Widgets/RepDashboardSubmitQueryContext';
import { BrandingProvider } from '@trinity/chat-components/containers/Branding';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { AudioControls } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/AudioControls';
import { useTextToSpeechStateContextNullSafe } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useModal } from '@trinity/chat-components/hooks/useModal';
import {
  ActivePageContext,
  MessagesListRefContext,
  ChatHasAccessContext,
  ChatSessionItemsContext,
  ChatLandingCardsVisibleContext,
  ActiveChatSessionContext,
} from './context';
import { ChatGuidelinesModal } from './GuidelinesModal';
import { useChatPermissions } from './hooks/useChatPermissions';
import { UnsubmittedMessage } from './Input';
import { LandingCards } from './LandingCards';
import { messageLaneWidth } from './Message.styles';
import { InitialSystemMessage } from './Message.System.Initial';
import { MessageList } from './MessageList';
import { NoAccessBanner } from './NoAccessBanner';

type Props = {
  className?: string;
  isSidePanel?: boolean;
};

type AgentContextDataWithMode = Pick<ChatContextItem, 'subject'> & {
  mode?: string;
  hideInputOnContextOpen?: boolean;
};

function computeChatVisibility(
  isSidePanel: boolean | undefined,
  isSidePanelExpanded: boolean,
  activePageId: number | null,
  hasExistingItems: boolean,
  chatPermissions: { canModify: boolean },
  disableChatInputWithLandingCards: boolean | undefined,
  showLandingCards: boolean,
  hideInputOnContextOpen: boolean
) {
  const showPageArea = !isSidePanel && (isSidePanelExpanded || activePageId != null);
  const showMessages =
    (isSidePanel && isSidePanelExpanded) || activePageId == null || (!isSidePanel && hasExistingItems);
  const showInput =
    !(!isSidePanel && isSidePanelExpanded) &&
    chatPermissions.canModify &&
    !hideInputOnContextOpen &&
    !(disableChatInputWithLandingCards && showLandingCards);
  return { showPageArea, showMessages, showInput };
}

function shouldShowChatGuidelines(chatGuidelines: string | null | undefined): boolean {
  return !!chatGuidelines;
}

function getMessageListContent(
  isLinkInitialized: boolean,
  showLandingCards: boolean,
  items: React.ComponentProps<typeof MessageList>['items']
): React.ReactNode {
  if (!isLinkInitialized) return null;
  if (showLandingCards) return <LandingCards />;
  if (!items.length) return <InitialSystemMessage />;
  return <MessageList items={items} />;
}

function getAudioPlayerContent(showAudioPlayer: boolean): React.ReactNode {
  if (!showAudioPlayer) return null;

  return (
    <AudioPlayerRow>
      <AudioPlayerBox>
        <AudioPlayerController>
          {({
            duration,
            position,
            isLoading,
            onScan,
            onSeek,
            onStop,
            onPreviousTrack,
            onNextTrack,
            onSkipBackward,
            onSkipAhead,
            playing,
            onTogglePlay,
            canSkipBackward,
            canSkipForward,
          }) => (
            <AudioControls
              isPlaying={playing}
              isLoading={isLoading}
              onTogglePlay={onTogglePlay}
              onPreviousTrack={onPreviousTrack}
              onNextTrack={onNextTrack}
              onSkipBackward={onSkipBackward}
              onSkipAhead={onSkipAhead}
              disabledPreviousTrack={!canSkipBackward}
              disabledNextTrack={!canSkipForward}
              onStop={onStop}
              currentTime={position}
              duration={duration}
              onScan={onScan}
              onSeek={onSeek}
            />
          )}
        </AudioPlayerController>
      </AudioPlayerBox>
    </AudioPlayerRow>
  );
}

function ChatPageArea({
  isSidePanel,
  fillWidth,
  codeWidgetHostActions,
  handleRepProfileClick,
  handleRepSubmitQuery,
  activePageId,
  proxyFetch,
  brandIdentifier,
  activeAgentContextData,
}: {
  isSidePanel?: boolean;
  fillWidth?: boolean;
  codeWidgetHostActions: CodeWidgetHostActions;
  handleRepProfileClick: (profileId: string, name: string) => void;
  handleRepSubmitQuery: (query: string) => void;
  activePageId: number | null;
  proxyFetch: (data: {
    agentId: number;
    extensionPath: string;
    brandIdentifier?: string;
  }) => Promise<CanonicalWidgetData | null>;
  brandIdentifier: string | null;
  activeAgentContextData: AgentContextDataWithMode | null;
}) {
  let PageRendererRoot = FullPageRenderer;
  if (isSidePanel) PageRendererRoot = SidePanelPageRenderer;
  else if (fillWidth) PageRendererRoot = FillPageRenderer;
  return (
    <PageArea>
      <CodeWidgetHostActionsContext.Provider value={codeWidgetHostActions}>
        <RepDashboardProfileClickContext.Provider value={handleRepProfileClick}>
          <RepDashboardSubmitQueryContext.Provider value={handleRepSubmitQuery}>
            <PageRendererRoot>
              <CustomPageContextContainer>
                <PageRenderer
                  pageId={activePageId}
                  proxyFetch={proxyFetch}
                  brandIdentifier={brandIdentifier ?? undefined}
                />
              </CustomPageContextContainer>
            </PageRendererRoot>
          </RepDashboardSubmitQueryContext.Provider>
        </RepDashboardProfileClickContext.Provider>
      </CodeWidgetHostActionsContext.Provider>
    </PageArea>
  );
}

function ChatFooter({
  audioPlayer,
  showInput,
  showChatGuidelines,
  toggleGuidelinesModal,
}: {
  audioPlayer: React.ReactNode;
  showInput: boolean;
  showChatGuidelines: boolean;
  toggleGuidelinesModal: () => void;
}) {
  return (
    <Footer>
      {audioPlayer}
      {showInput && <UnsubmittedMessage />}
      {showChatGuidelines && (
        <DisclaimerContainer>
          <DisclaimerCopy>
            Shared content is meant for internal use only and not meant to be shared externally.
          </DisclaimerCopy>
          <OpenDisclaimer onClick={toggleGuidelinesModal}>
            View Usage Guidelines <Info size={16} />
          </OpenDisclaimer>
        </DisclaimerContainer>
      )}
    </Footer>
  );
}

export const ChatInstance = (props: Props) => {
  const hasAccess = useContext(ChatHasAccessContext);
  const link = useContext(LinkContext);
  const chatPermissions = useChatPermissions();

  const [, setMessagesRef] = useContext(MessagesListRefContext);
  const [toggleGuidelinesModal, GuidelinesModal] = useModal(ChatGuidelinesModal);
  const features = useContext(GroupFeaturesContext);
  const chatGuidelines = features?.chatGuidelines;
  const chatGuidelinesText = chatGuidelines ?? '';
  const showChatGuidelines = shouldShowChatGuidelines(chatGuidelines);
  const disableChatInputWithLandingCards = features?.disableChatInputWithLandingCards;

  const items = useContext(ChatSessionItemsContext);

  const showLandingCards = useContext(ChatLandingCardsVisibleContext);

  const { pageId: activePageId, clearPage } = useContext(ActivePageContext);
  const apiClient = useChatApiClient();
  const chat = useContext(ActiveChatSessionContext);
  const activeAgentContextData: AgentContextDataWithMode | null =
    chat.context.type === ChatContextType.Agent ? chat.context.data : null;
  const hideInputOnContextOpen = activeAgentContextData?.hideInputOnContextOpen ?? false;
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const proxyFetch = useCallback(
    ({ agentId, extensionPath }: { agentId: number; extensionPath: string }) =>
      apiClient.pages.proxyWidgetData(agentId, extensionPath, brandIdentifier ?? undefined),
    [apiClient, brandIdentifier]
  );
  const [isSidePanelExpanded] = useContext(SidePanelExpandedContext);

  const handleRepProfileClick = useCallback(
    (profileId: string, name: string) => {
      clearPage();
      if (chat.context.type !== ChatContextType.Agent || !brandIdentifier) return;

      dispatch({
        type: 'new-chat-started',
        payload: {
          brandIdentifier,
          context: {
            ...chat.context,
            data: {
              ...chat.context.data,
              subject: {
                id: profileId,
                name,
              },
            },
          },
          pendingQuery: `Show me more about ${name}`,
        },
      });
    },
    [clearPage, chat.context, brandIdentifier, dispatch]
  );

  const handleRepSubmitQuery = useCallback(
    (query: string) => {
      clearPage();
      if (chat.context.type !== ChatContextType.Agent || !brandIdentifier) return;

      dispatch({
        type: 'new-chat-started',
        payload: {
          brandIdentifier,
          context: chat.context,
          pendingQuery: query,
        },
      });
    },
    [clearPage, chat.context, brandIdentifier, dispatch]
  );

  const messageListContent = getMessageListContent(link.initialized, showLandingCards, items);

  const showAudioPlayer = useShowAudioPlayer();

  const audioPlayer = getAudioPlayerContent(showAudioPlayer);

  const codeWidgetHostActions = useMemo<CodeWidgetHostActions>(
    () => ({ submitQuery: handleRepSubmitQuery, viewProfile: handleRepProfileClick }),
    [handleRepSubmitQuery, handleRepProfileClick]
  );

  const { showPageArea, showMessages, showInput } = computeChatVisibility(
    props.isSidePanel,
    isSidePanelExpanded,
    activePageId,
    items.length > 0,
    chatPermissions,
    disableChatInputWithLandingCards,
    showLandingCards,
    hideInputOnContextOpen
  );

  const pageArea = showPageArea ? (
    <ChatPageArea
      isSidePanel={props.isSidePanel}
      codeWidgetHostActions={codeWidgetHostActions}
      handleRepProfileClick={handleRepProfileClick}
      handleRepSubmitQuery={handleRepSubmitQuery}
      activePageId={activePageId}
      proxyFetch={proxyFetch}
      brandIdentifier={brandIdentifier}
      activeAgentContextData={activeAgentContextData}
    />
  ) : null;
  const messagesArea = showMessages ? (
    <Messages>
      <MessagesWrap ref={setMessagesRef}>{messageListContent}</MessagesWrap>
    </Messages>
  ) : null;
  const footer = (
    <ChatFooter
      audioPlayer={audioPlayer}
      showInput={showInput}
      showChatGuidelines={showChatGuidelines}
      toggleGuidelinesModal={toggleGuidelinesModal}
    />
  );

  return (
    <BrandingProvider>
      <ChatInstanceRoot className={props.className}>
        {!hasAccess && <NoAccessBanner />}

        {pageArea}
        {messagesArea}
        {footer}

        {showChatGuidelines && <GuidelinesModal guidelines={chatGuidelinesText} />}
      </ChatInstanceRoot>
    </BrandingProvider>
  );
};

function useShowAudioPlayer(): boolean {
  const features = useContext(GroupFeaturesContext);
  const audioPlayerState = useTextToSpeechStateContextNullSafe();
  const audioPlayerEnabled = Boolean(
    features?.chatReadAloud && features?.chatReadAloudTtsConfig.type !== 'browser'
  );

  return useMemo(
    () => Boolean(audioPlayerEnabled && (!!audioPlayerState.queryId || !!audioPlayerState.kolSearchId)),
    [audioPlayerEnabled, audioPlayerState]
  );
}

const MaxContainerWidth = 1100;

export const DisclaimerContainer = styled.div({
  width: messageLaneWidth,
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  containerType: 'inline-size',
  fontSize: 14,
});

const DisclaimerCopy = styled.span({
  textAlign: 'center',
  '@container (max-width: 640px)': {
    display: 'none',
  },
});

const OpenDisclaimer = styled.div(({ theme }) => ({
  display: 'inline-flex',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  marginLeft: 10,
  alignItems: 'center',
  gap: 4,
  '@container (max-width: 640px)': {
    marginLeft: 0,
    marginTop: 4,
  },
}));

export const ChatInstanceRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100% - ${Constants.Modal.HeaderHeight}px)`,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
});

const Messages = styled.div({
  boxSizing: 'border-box',
  flex: 2,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
  backgroundAttachment: 'local, local, scroll, scroll',
});

const PageArea = styled.div({
  boxSizing: 'border-box',
  flex: 3,
  minHeight: 0,
  overflow: 'hidden',
});

export const MessagesWrap = styled.div({
  minHeight: '100%',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
  paddingTop: 15,
  paddingRight: 10,
  paddingLeft: 10,
  boxSizing: 'border-box',
});

export const Footer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: `20px 10px ${15}px 10px`,
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
});

const AudioPlayerRow = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'absolute',
  right: 9,
  bottom: `calc(100% - 8px)`,
});

const AudioPlayerBox = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: theme.palette.white.main,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  padding: 10,
  width: 'auto',
  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

const FullPageRenderer = styled(Box)(({ theme }) => ({
  width: '95%',
  maxWidth: '95%',
  height: 'calc(100% - 24px)',
  margin: '0 auto',
  marginTop: '24px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const SidePanelPageRenderer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.gray.light2}`,
  borderTop: `2px solid ${theme.palette.gray.light2}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const FillPageRenderer = styled(Box)({
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});
